import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@newfront-insurance/core-ui/v2';
import { useId, useState, type ComponentType } from 'react';

import type { SettingsEmployee } from './employee-utils';
import { SettingsPeoplePicker, type PeoplePickerProps, type SettingsPeopleEligibility } from './people-picker';
import { useSelectionSubmission } from '../settings-shared/use-selection-submission';

export interface SettingsPeopleSubmitResult {
  failedUserUuids: readonly string[];
  error?: string;
}

/** Shared dialog presentation. Its caller owns eligibility, mutations, and refreshes. Mount only while open. */
export function SettingsPeopleDialog({
  title = 'Add people',
  description,
  hideDescription = false,
  submitLabel = 'Add people',
  disabled = false,
  excludedUserUuids,
  eligibility,
  onClose,
  onSubmit,
  picker: Picker = SettingsPeoplePicker,
  inputLabel = 'Add teammates',
  pendingLabel = 'Adding...',
}: {
  title?: string;
  description: string;
  hideDescription?: boolean;
  submitLabel?: string;
  disabled?: boolean;
  excludedUserUuids: Set<string>;
  eligibility?: SettingsPeopleEligibility;
  onClose: () => void;
  onSubmit: (employees: SettingsEmployee[]) => Promise<SettingsPeopleSubmitResult>;
  picker?: ComponentType<PeoplePickerProps>;
  inputLabel?: string;
  pendingLabel?: string;
}) {
  const inputId = useId();
  const [selectedEmployees, setSelectedEmployees] = useState<SettingsEmployee[]>([]);
  const isSubmitDisabled = disabled || !!eligibility?.loading || !!eligibility?.error;
  const { isSaving, error, submit, close } = useSelectionSubmission({
    selected: selectedEmployees,
    getKey: (employee) => employee.userUuid,
    disabled: isSubmitDisabled,
    onSubmit: async (employees) => {
      const result = await onSubmit(employees);
      return { failedKeys: result.failedUserUuids, error: result.error };
    },
    onSettled: (failed) => setSelectedEmployees(failed),
    onClose,
    partialFailureMessage: "Couldn't save changes for everyone. Your remaining selections are kept. Try again.",
    failureMessage: "Couldn't save changes. Your selections are kept. Try again.",
  });

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <AlertDialogContent className="w-[398px] max-w-[calc(100vw-32px)] gap-5 rounded-[4px] p-6 font-['Open_Sans',sans-serif]">
        <AlertDialogHeader className="flex-col">
          <AlertDialogTitle className="text-steel-500 text-base leading-6 font-semibold">{title}</AlertDialogTitle>
          <AlertDialogDescription className={hideDescription ? 'sr-only' : 'text-steel-400 text-sm'}>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={inputId} className="text-steel-500 text-xs leading-4 font-semibold">
            {inputLabel}
          </label>
          <Picker
            inputId={inputId}
            excludedUserUuids={excludedUserUuids}
            eligibility={eligibility}
            selectedEmployees={selectedEmployees}
            disabled={disabled || isSaving}
            onAddEmployee={(employee) =>
              setSelectedEmployees((current) =>
                current.some((item) => item.userUuid === employee.userUuid) ? current : [...current, employee],
              )
            }
            onRemoveEmployee={(userUuid) =>
              setSelectedEmployees((current) => current.filter((item) => item.userUuid !== userUuid))
            }
          />
        </div>
        {error ? (
          <p role="alert" className="text-fire-500 text-sm">
            {error}
          </p>
        ) : null}
        <AlertDialogFooter className="flex-row justify-end gap-2">
          <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isSubmitDisabled || !selectedEmployees.length || isSaving}
            onClick={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            {isSaving ? pendingLabel : submitLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
